import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Heart, Flower2, Sun, ArrowRight, Check, LogIn, Gift } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { API } from '@/App';
import TestimonialsSection from '@/components/TestimonialsSection';

function LandingPage({ scrollToPricing = false }) {
  const navigate = useNavigate();
  const [checkingAuth, setCheckingAuth] = useState(true);

  useEffect(() => {
    // Check if user is logged in with active subscription
    const checkAuthAndRedirect = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const response = await axios.get(`${API}/user/profile`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          // If user has active subscription, redirect to dashboard
          if (response.data.subscription_active) {
            navigate('/dashboard');
            return;
          }
        } catch (error) {
          // Token invalid or expired, clear it
          if (error.response?.status === 401) {
            localStorage.clear();
          }
        }
      }
      setCheckingAuth(false);
    };
    
    checkAuthAndRedirect();
  }, [navigate]);

  useEffect(() => {
    if (scrollToPricing && !checkingAuth) {
      document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [scrollToPricing, checkingAuth]);

  // Show loading while checking auth
  if (checkingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Sparkles className="w-12 h-12 text-primary animate-pulse" />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Fixed Header with Sign In */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md shadow-sm">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Flower2 className="w-8 h-8 text-primary" />
            <span className="text-xl font-serif font-bold text-secondary-900">Radiant 40</span>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/articles')}
              className="text-secondary-600 hover:text-primary transition-colors font-medium"
            >
              Articles
            </button>
            <button
              onClick={() => navigate('/recipes')}
              className="text-secondary-600 hover:text-primary transition-colors font-medium"
            >
              Recipes
            </button>
            <Button
              data-testid="header-sign-in-button"
              onClick={() => navigate('/auth')}
              variant="outline"
              className="border-2 border-primary text-primary hover:bg-primary hover:text-white px-6 py-2 rounded-full transition-all duration-300"
            >
              <LogIn className="w-4 h-4 mr-2" />
              Sign In
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-gradient min-h-screen flex items-center justify-center relative overflow-hidden pt-20" data-testid="hero-section">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-10 animate-bounce" style={{ animationDelay: '0s', animationDuration: '3s' }}>
            <Flower2 className="w-16 h-16 text-secondary" />
          </div>
          <div className="absolute top-40 right-20 animate-bounce" style={{ animationDelay: '1s', animationDuration: '4s' }}>
            <Sun className="w-20 h-20 text-primary" />
          </div>
          <div className="absolute bottom-32 left-1/4 animate-bounce" style={{ animationDelay: '2s', animationDuration: '3.5s' }}>
            <Sparkles className="w-12 h-12 text-accent" />
          </div>
        </div>

        <div className="container mx-auto px-6 py-24 relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-8" data-testid="hero-content">
              <div className="inline-block">
                <span className="px-6 py-2 bg-white/80 backdrop-blur-sm rounded-full text-primary font-semibold text-sm tracking-wide shadow-md">
                  12-Week Transformation Program
                </span>
              </div>
              <h1 className="text-5xl md:text-7xl font-serif font-bold text-secondary-900 leading-tight">
                Radiant 40
              </h1>
              <p className="text-2xl md:text-3xl text-secondary-800 font-light">
                Reclaim Your Radiance and Vitality
              </p>
              <p className="text-lg text-secondary-700 leading-relaxed max-w-xl">
                A holistic 12-week journey designed for women in their 40s who want to feel healthier, more vibrant, and beautiful—inside and out.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  data-testid="get-started-button"
                  onClick={() => navigate('/pricing')}
                  className="bg-primary hover:bg-primary/90 text-white px-8 py-6 text-lg rounded-full shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300"
                >
                  Begin Your Journey <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button
                  data-testid="learn-more-button"
                  onClick={() => document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' })}
                  variant="outline"
                  className="border-2 border-primary text-primary hover:bg-primary/5 px-8 py-6 text-lg rounded-full transition-all duration-300"
                >
                  Learn More
                </Button>
              </div>
            </div>
            <div className="relative" data-testid="hero-image">
              <div className="rounded-3xl overflow-hidden shadow-2xl transform hover:scale-105 transition-transform duration-500">
                <img
                  src="https://images.pexels.com/photos/13067132/pexels-photo-13067132.jpeg"
                  alt="Radiant woman smiling in sunflower garden"
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-white" data-testid="about-section">
        <div className="container mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-secondary-900 mb-6">
              Your Holistic Journey
            </h2>
            <p className="text-lg text-secondary-700 leading-relaxed">
              Radiant 40 helps you reconnect with your best self — mind, body, and spirit. The result? You feel amazing, and it shows.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div
              data-testid="feature-card-0"
              className="bg-gradient-to-b from-white to-background p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border-2 border-accent/30 transform hover:-translate-y-2"
            >
              <div className="bg-accent/10 w-16 h-16 rounded-full flex items-center justify-center mb-6 text-accent">
                <Sparkles className="w-10 h-10" />
              </div>
              <h3 className="text-2xl font-serif font-bold text-secondary-900 mb-4">Mental Clarity</h3>
              <p className="text-secondary-700 leading-relaxed">Sharpen your mind, reduce stress, and cultivate inner peace through mindfulness and focus.</p>
            </div>

            <div
              data-testid="feature-card-1"
              className="bg-gradient-to-b from-white to-background p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border-2 border-secondary/30 transform hover:-translate-y-2"
            >
              <div className="bg-secondary/10 w-16 h-16 rounded-full flex items-center justify-center mb-6 text-secondary">
                <Heart className="w-10 h-10" />
              </div>
              <h3 className="text-2xl font-serif font-bold text-secondary-900 mb-4">Emotional Well-being</h3>
              <p className="text-secondary-700 leading-relaxed">Nurture your emotional health, build resilience, and embrace self-love and compassion.</p>
            </div>

            <div
              data-testid="feature-card-2"
              className="bg-gradient-to-b from-white to-background p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border-2 border-accent/30 transform hover:-translate-y-2"
            >
              <div className="bg-accent/10 w-16 h-16 rounded-full flex items-center justify-center mb-6 text-accent">
                <Flower2 className="w-10 h-10" />
              </div>
              <h3 className="text-2xl font-serif font-bold text-secondary-900 mb-4">Physical Vitality</h3>
              <p className="text-secondary-700 leading-relaxed">Improve nutrition, movement, skincare, and sleep for a healthier, more radiant you.</p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 bg-gradient-to-b from-secondary-50 to-accent-50" data-testid="how-it-works-section">
        <div className="container mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-secondary-900 mb-6">
              How It Works
            </h2>
            <p className="text-lg text-secondary-700">
              A simple, rewarding journey that builds momentum week by week.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { step: '1', title: 'Sign Up', description: 'Choose your plan and create your account' },
              { step: '2', title: 'Weekly Themes', description: 'Each week focuses on a new wellness aspect' },
              { step: '3', title: 'Daily Tasks', description: 'Complete small, achievable daily activities' },
              { step: '4', title: 'Grow Your Garden', description: 'Watch your beautiful garden bloom with progress' }
            ].map((item, index) => (
              <div key={index} data-testid={`step-${index}`} className="relative">
                <div className="bg-white p-8 rounded-2xl shadow-md hover:shadow-lg transition-shadow">
                  <div className="bg-primary text-white w-14 h-14 rounded-full flex items-center justify-center text-2xl font-bold font-serif mb-6 shadow-lg">
                    {item.step}
                  </div>
                  <h3 className="text-xl font-serif font-bold text-secondary-900 mb-3">{item.title}</h3>
                  <p className="text-secondary-700">{item.description}</p>
                </div>
                {index < 3 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                    <ArrowRight className="w-8 h-8 text-primary" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Founding Members Banner */}
      <section className="py-12 bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-400" data-testid="founding-members-section">
        <div className="container mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-block bg-white/20 backdrop-blur-sm px-4 py-1 rounded-full text-amber-900 font-semibold text-sm mb-4">
              🎉 LIMITED TIME OFFER
            </div>
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-amber-900 mb-4">
              First 50 Members Get FREE Access!
            </h2>
            <p className="text-lg text-amber-800 mb-6">
              Be a founding member and get the complete 12-week Radiant 40 program absolutely free. Limited spots available!
            </p>
            <Button
              data-testid="founding-member-button"
              onClick={() => navigate('/register-free')}
              className="bg-amber-900 hover:bg-amber-800 text-white px-10 py-6 text-lg rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 font-bold"
            >
              <Gift className="w-5 h-5 mr-2" />
              Claim Your Free Spot
            </Button>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-24 bg-gradient-to-b from-primary-50 to-secondary-50" data-testid="pricing-section">
        <div className="container mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-secondary-900 mb-6">
              Choose Your Path
            </h2>
            <p className="text-lg text-secondary-700">
              Invest in yourself and your well-being
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Monthly Plan */}
            <div data-testid="pricing-monthly" className="bg-white rounded-3xl shadow-xl p-10 border-2 border-primary/20 hover:border-primary transition-all duration-300 transform hover:-translate-y-2">
              <div className="text-center mb-8">
                <h3 className="text-3xl font-serif font-bold text-secondary-900 mb-2">Monthly</h3>
                <p className="text-secondary-600 mb-6">Perfect for getting started</p>
                <div className="mb-6">
                  <span className="text-5xl font-serif font-bold text-primary">$9.99</span>
                  <span className="text-secondary-600"> / 4 weeks</span>
                </div>
              </div>
              <ul className="space-y-4 mb-8">
                {[
                  'Access 4 weeks at a time',
                  '2 recurring payments',
                  'Daily achievable tasks',
                  'Interactive garden visualization',
                  'Progress tracking'
                ].map((feature, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <Check className="w-6 h-6 text-accent shrink-0 mt-0.5" />
                    <span className="text-secondary-700">{feature}</span>
                  </li>
                ))}
              </ul>
              <Button
                data-testid="pricing-monthly-button"
                onClick={() => navigate('/auth')}
                className="w-full bg-primary hover:bg-primary/90 text-white py-6 text-lg rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
              >
                Start Monthly Plan
              </Button>
            </div>

            {/* Full Course Plan */}
            <div data-testid="pricing-full" className="bg-gradient-to-br from-secondary to-secondary-600 rounded-3xl shadow-2xl p-10 border-2 border-secondary transform md:-translate-y-4 relative">
              <div className="text-center mb-8">
                <h3 className="text-3xl font-serif font-bold text-white mb-2">Full Course</h3>
                <p className="text-secondary-100 mb-6">Complete transformation journey</p>
                <div className="mb-6">
                  <span className="text-5xl font-serif font-bold text-white">$24.99</span>
                  <span className="text-secondary-100"> / 12 weeks</span>
                </div>
              </div>
              <ul className="space-y-4 mb-8">
                {[
                  'Full 12-week program access',
                  'Daily achievable tasks',
                  'Interactive garden visualization',
                  'Progress tracking',
                  'One-time payment'
                ].map((feature, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <Check className="w-6 h-6 text-white shrink-0 mt-0.5" />
                    <span className="text-white">{feature}</span>
                  </li>
                ))}
              </ul>
              <Button
                data-testid="pricing-full-button"
                onClick={() => navigate('/auth')}
                className="w-full bg-white hover:bg-secondary-50 text-secondary py-6 text-lg rounded-full shadow-lg hover:shadow-xl transition-all duration-300 font-bold"
              >
                Start Full Course
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Join Community CTA */}
      <section className="py-16 bg-gradient-to-br from-secondary-50 via-primary-50 to-accent-50">
        <div className="container mx-auto px-6">
          <div 
            onClick={() => navigate('/auth')}
            className="max-w-4xl mx-auto bg-white rounded-3xl shadow-2xl p-12 cursor-pointer transform hover:scale-105 transition-all duration-300 hover:shadow-3xl"
            data-testid="join-community-cta"
          >
            <div className="flex flex-col md:flex-row items-center justify-between gap-8">
              <div className="flex items-center gap-6">
                <div className="bg-secondary/20 p-6 rounded-full">
                  <Heart className="w-12 h-12 text-secondary" />
                </div>
                <div>
                  <h3 className="text-3xl font-serif font-bold text-secondary-900 mb-2">Women Transformed</h3>
                  <p className="text-lg text-secondary-700">Join our community and start your journey</p>
                </div>
              </div>
              <Button
                data-testid="join-community-button"
                className="bg-primary hover:bg-primary/90 text-white px-8 py-6 text-lg rounded-full shadow-lg hover:shadow-xl transition-all duration-300 shrink-0"
              >
                Join Now <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <TestimonialsSection />

      {/* Footer */}
      <footer className="bg-secondary-900 text-white py-12">
        <div className="container mx-auto px-6">
          <div className="text-center mb-8">
            <h3 className="text-3xl font-serif font-bold mb-4">Radiant 40</h3>
            <p className="text-secondary-300 mb-6">Rediscover your radiance, one day at a time.</p>
          </div>
          
          <div className="flex flex-wrap justify-center gap-6 mb-8">
            <button 
              onClick={() => navigate('/privacy')}
              className="text-secondary-300 hover:text-white transition-colors"
            >
              Privacy Policy
            </button>
            <button 
              onClick={() => navigate('/terms')}
              className="text-secondary-300 hover:text-white transition-colors"
            >
              Terms of Service
            </button>
            <a 
              href="mailto:sundog.rose@gmail.com"
              className="text-secondary-300 hover:text-white transition-colors"
            >
              Contact Us
            </a>
          </div>
          
          <p className="text-secondary-400 text-sm text-center">
            © 2026 Sundog Rosie. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}

export default LandingPage;